import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { ItemValidationBadges } from '../components/ItemValidationBadges'

const box = { background: '#161d31', borderRadius: 12, padding: 16, marginBottom: 16 }
const btn = { background: '#4f46e5', color: 'white', border: 0, borderRadius: 8, padding: '10px 14px', cursor: 'pointer' }
const subtleBtn = { ...btn, background: '#334155' }
const input = { width: '100%', padding: 10, borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: 'white' }
const label = { display: 'block', fontSize: 14, color: '#94a3b8', marginBottom: 6 }

export default function TriageQueuePage() {
  const navigate = useNavigate()
  const [imports, setImports] = useState([])
  const [selectedImport, setSelectedImport] = useState(null)
  const [items, setItems] = useState([])
  const [categories, setCategories] = useState([])
  const [vtneDomains, setVtneDomains] = useState([])
  const [bulkCategoryId, setBulkCategoryId] = useState('')
  const [bulkVtneDomainId, setBulkVtneDomainId] = useState('')
  const [selectedIds, setSelectedIds] = useState([])
  const [working, setWorking] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    if (selectedImport?.id) loadItems(selectedImport.id)
  }, [selectedImport])

  async function loadData() {
    const [importsData, catsData, domainsData] = await Promise.all([
      api.importRuns(),
      api.categories(),
      api.vtneDomains(),
    ])
    setImports(importsData)
    setCategories(catsData)
    setVtneDomains(domainsData)
    if (importsData.length) setSelectedImport(importsData[0])
  }

  async function loadItems(importId) {
    const data = await api.importItems(importId, {})
    // Filter to items that need triage (not yet approved)
    const needsTriage = data.items.filter(i => i.review_status === 'pending' || i.review_status === 'needs_review')
    setItems(needsTriage)
    setSelectedIds([])
  }

  async function bulkAction(action) {
    if (selectedIds.length === 0 || !selectedImport) return
    setWorking(true)
    setMessage('')
    setError('')
    try {
      const payload = { action, item_ids: selectedIds }
      if (action === 'assign_category') payload.category_id = Number(bulkCategoryId)
      if (action === 'assign_vtne_domain') payload.vtne_domain_id = Number(bulkVtneDomainId)

      const result = await api.bulkImportItems(selectedImport.id, payload)
      await loadItems(selectedImport.id)

      const actionName = action.replace(/_/g, ' ')
      setMessage(`✓ ${actionName}: updated ${result.updated_count} item(s)`)
    } catch (err) {
      setError(String(err))
    } finally {
      setWorking(false)
    }
  }

  function toggleSelected(id) {
    setSelectedIds(current => current.includes(id) ? current.filter(v => v !== id) : [...current, id])
  }

  function toggleSelectAll() {
    if (selectedIds.length === items.length) {
      setSelectedIds([])
    } else {
      setSelectedIds(items.map(i => i.id))
    }
  }

  const categoryName = bulkCategoryId ? categories.find(c => Number(c.id) === Number(bulkCategoryId))?.name : ''
  const domainName = bulkVtneDomainId ? vtneDomains.find(d => Number(d.id) === Number(bulkVtneDomainId))?.name : ''

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <a href="/imports" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: 13 }}>← Back to imports</a>
      </div>

      <h1 style={{ marginTop: 0, marginBottom: 20 }}>Triage Queue</h1>

      {message && <div style={{ ...box, background: '#052e16', color: '#86efac', marginBottom: 16 }}>{message}</div>}
      {error && <div style={{ ...box, background: '#450a0a', color: '#fca5a5', marginBottom: 16 }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 20 }}>
        {/* Sidebar: Import selection */}
        <div>
          <div style={box}>
            <h3 style={{ marginTop: 0, marginBottom: 12 }}>Imports</h3>
            {imports.length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: 13 }}>No imports</div>
            ) : (
              imports
                .filter(i => i.status === 'parsed')
                .map(imp => (
                  <button
                    key={imp.id}
                    onClick={() => setSelectedImport(imp)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: 10,
                      marginBottom: 8,
                      background: selectedImport?.id === imp.id ? '#1e3a8a' : '#0f172a',
                      border: selectedImport?.id === imp.id ? '2px solid #4f46e5' : '1px solid #334155',
                      color: 'white',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontSize: 12,
                    }}
                  >
                    <div style={{ fontWeight: selectedImport?.id === imp.id ? 700 : 500 }}>
                      {imp.file_name.slice(0, 20)}
                    </div>
                    <div style={{ fontSize: 10, color: '#cbd5e1', marginTop: 2 }}>
                      {imp.item_count} items
                    </div>
                  </button>
                ))
            )}
          </div>
        </div>

        {/* Main area: Triage controls and items */}
        <div>
          {!selectedImport ? (
            <div style={box}>
              <h2 style={{ marginTop: 0 }}>No Import Selected</h2>
              <p style={{ color: '#94a3b8' }}>Select a parsed import from the left to triage items.</p>
            </div>
          ) : (
            <>
              {/* Quick actions */}
              <div style={box}>
                <h3 style={{ marginTop: 0, marginBottom: 16 }}>Quick Triage Actions</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                  <div>
                    <label style={label}>Assign category</label>
                    <select style={input} value={bulkCategoryId} onChange={e => setBulkCategoryId(e.target.value)}>
                      <option value="">Choose…</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={label}>Assign VTNE domain</label>
                    <select style={input} value={bulkVtneDomainId} onChange={e => setBulkVtneDomainId(e.target.value)}>
                      <option value="">Choose…</option>
                      {vtneDomains.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    style={btn}
                    onClick={() => bulkAction('assign_category')}
                    disabled={working || selectedIds.length === 0 || !bulkCategoryId}
                  >
                    {categoryName ? `Assign category to ${selectedIds.length}` : 'Select category first'}
                  </button>
                  <button
                    style={btn}
                    onClick={() => bulkAction('assign_vtne_domain')}
                    disabled={working || selectedIds.length === 0 || !bulkVtneDomainId}
                  >
                    {domainName ? `Assign domain to ${selectedIds.length}` : 'Select domain first'}
                  </button>
                  <button
                    style={subtleBtn}
                    onClick={toggleSelectAll}
                    disabled={items.length === 0}
                  >
                    {selectedIds.length === items.length ? 'Clear' : 'Select'} All
                  </button>
                </div>

                <div style={{ marginTop: 12, fontSize: 13, color: '#94a3b8' }}>
                  {selectedIds.length} of {items.length} selected
                </div>
              </div>

              {/* Item list */}
              <div style={box}>
                <h3 style={{ marginTop: 0, marginBottom: 12 }}>Items Needing Triage ({items.length})</h3>
                {items.length === 0 ? (
                  <div style={{ color: '#86efac' }}>✓ All items triaged for this import!</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {items.map(item => (
                      <div
                        key={item.id}
                        style={{
                          display: 'flex',
                          gap: 12,
                          alignItems: 'flex-start',
                          padding: 12,
                          background: '#0f172a',
                          border: '1px solid #334155',
                          borderRadius: 8,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(item.id)}
                          onChange={() => toggleSelected(item.id)}
                          style={{ marginTop: 4, cursor: 'pointer' }}
                        />
                        <div style={{ flex: 1 }}>
                          <button
                            onClick={() => navigate(`/imports?item=${item.id}`)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#cbd5e1',
                              textAlign: 'left',
                              cursor: 'pointer',
                              fontSize: 13,
                              fontWeight: 700,
                              padding: 0,
                            }}
                          >
                            {item.proposed_admin_title || 'Untitled'}
                          </button>
                          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4, lineHeight: 1.4 }}>
                            {(item.proposed_stem || '').slice(0, 100)}…
                          </div>
                          <div style={{ marginTop: 6 }}>
                            <ItemValidationBadges item={item} compact={true} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
